// src/area/InstitucionesArea/InstitucionesArea.jsx
import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { FiDownloadCloud, FiEdit3, FiRefreshCw, FiX } from 'react-icons/fi'
import API_BASE_URL from '../../config/config.js'
import { formatIdSport } from '../../utils/formatIdSport.js'
import { toast } from 'react-hot-toast'
import {
    getCumulativeSeriesAvailability,
    recalculateCumulativeEvent,
} from '../../services/cumulativeSeriesService.js'
import ImportAccreditationsModal from './ImportAccreditationsModal.jsx'
import ModalSportStatisticalReport from './ModalSportStatisticalReport.jsx'
import {
    downloadSportStatisticalReportPdf,
    getSportReportOptions,
    triggerBlobDownload,
} from './sportReportsService.js'
import { getFichaAuxiliarSisdeuEventId } from './fichaAuxiliarService.js'
import SisdeuImportPanel, { SISDEU_PANEL_KINDS } from './SisdeuImportPanel.jsx'

const PRIMARY = '#1e88e5'   // -> rgb(30 136 229)
const PRIMARY_DARK = '#1976d2'

// FTR-0006: participants.displayName — vacío/espacios => null (usa el nombre de la institución)
const DISPLAY_NAME_MAX_LENGTH = 255
const normalizeDisplayName = value =>
    typeof value === 'string' && value.trim() !== '' ? value.trim() : null

const Spinner = ({ className = 'h-4 w-4' }) => (
    <svg
        className={`${className} animate-spin`}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
)

// FTR-0049: `individual` = pestaña Instituciones de un deporte individual (typeSport 3): misma lista de participantes y
// mismo modal de acreditados, más "Instituciones SISDEU" cuando el backend lo habilita.
const InstitucionesArea = ({ individual = false }) => {
    const { idevent, idsport } = useParams()
    const { user } = useSelector(state => state.auth)

    const [instituciones, setInstituciones] = useState([])
    const [participantsLoaded, setParticipantsLoaded] = useState(false)
    const [participantsRevision, setParticipantsRevision] = useState(0)
    const [loadingId, setLoadingId] = useState(null)       // descarga PDF por institucion
    const [editMode, setEditMode] = useState(false)        // alterna modo editar
    const [savingId, setSavingId] = useState(null)         // guardando ranking por participante
    const [rankingValues, setRankingValues] = useState({}) // { idparticipant: string }
    const [displayNameValues, setDisplayNameValues] = useState({}) // { idparticipant: string } (FTR-0006)
    const [cumulativeSeries, setCumulativeSeries] = useState(null)
    const [isRecalculatingCumulative, setIsRecalculatingCumulative] = useState(false)

    const [isImportModalOpen, setIsImportModalOpen] = useState(false)
    const [selectedInstitution, setSelectedInstitution] = useState(null)

    const [reportOptions, setReportOptions] = useState(null)
    const [isReportModalOpen, setIsReportModalOpen] = useState(false)
    const [isReportButtonLoading, setIsReportButtonLoading] = useState(false)
    const isHandballBeach = formatIdSport(Number(idsport)) === 7

    // FTR-0052: informativo para la masiva. El backend decide qué instituciones procesa (las participantes, sin los
    // retirados con state -2); este número solo anticipa lo que ya muestra la tabla.
    const participatingCount = useMemo(
        () => (participantsLoaded ? instituciones.filter(p => Number(p?.state) !== -2).length : null),
        [instituciones, participantsLoaded]
    )

    const hasUnsavedRankingChanges = useMemo(() => {
        const normalizeRanking = value => value === null || value === undefined || value === '' ? '' : String(Number(value))
        return instituciones.some(participant => (
            normalizeRanking(rankingValues[participant.idparticipant]) !==
            normalizeRanking(participant.ranking)
        ))
    }, [instituciones, rankingValues])

    // Carga participantes
    useEffect(() => {
        const getParticipantes = async () => {
            try {
                const { data } = await axios.post(`${API_BASE_URL}/getparticipants`, {
                    idevent: +idevent,
                    idsport: +idsport,
                    idtypephase: 1,
                })
                setInstituciones(data || [])
                setParticipantsLoaded(true)
                // Pre-cargar valores de ranking (string para permitir vacío fácilmente)
                const map = {}
                const displayMap = {}
                    ; (data || []).forEach(p => {
                        map[p.idparticipant] = (p?.ranking ?? '') + '' // '' si null/undefined
                        displayMap[p.idparticipant] = p?.displayName ?? ''
                    })
                setRankingValues(map)
                setDisplayNameValues(displayMap)
            } catch (e) {
                console.error(e)
            }
        }
        if (idevent && idsport) getParticipantes()
    }, [idevent, idsport, participantsRevision])

    // Consulta ligera única: evita cargar todo el editor del circuito en este tab.
    useEffect(() => {
        let cancelled = false
        const loadAvailability = async () => {
            try {
                const payload = await getCumulativeSeriesAvailability({ idevent, idsport })
                if (!cancelled) setCumulativeSeries(payload?.available ? payload.series : null)
            } catch (error) {
                console.error('Error consultando circuito acumulado:', error)
                if (!cancelled) setCumulativeSeries(null)
            }
        }
        if (idevent && idsport) loadAvailability()
        return () => { cancelled = true }
    }, [idevent, idsport])

    const descargarInstitucion = async inst => {
        setLoadingId(inst.idinstitution)
        try {
            // FTR-0047: SISDEU recibe el idevent del vínculo activo (event_external_ids) o, sin vínculo, el idchampionship.
            const sisdeuEventId = await getFichaAuxiliarSisdeuEventId(idevent)
            const url =
                `https://sismaster.perufedup.com/pdf/auxiliar` +
                `?a=${btoa(sisdeuEventId)}&b=${btoa(idsport)}&c=${btoa(inst.idinstitution)}` +
                `&username=${user.username}`
            const { data, headers } = await axios.get(url, {
                responseType: 'arraybuffer',
                withCredentials: false,
                headers: { Accept: 'application/pdf' },
            })
            const blob = new Blob([data], { type: headers['content-type'] || 'application/pdf' })
            const link = Object.assign(document.createElement('a'), {
                href: URL.createObjectURL(blob),
                download: `${inst.abrev}_${idevent}.pdf`,
            })
            document.body.appendChild(link)
            link.click()
            link.remove()
            URL.revokeObjectURL(link.href)
        } catch (err) {
            console.error('Descarga fallida:', err)
            alert('No se pudo descargar el PDF.')
        } finally {
            setLoadingId(null)
        }
    }

    const openReportModal = async () => {
        if (!idevent || !idsport || isReportButtonLoading) return

        setIsReportButtonLoading(true)
        try {
            if (isHandballBeach) {
                const { blob, filename } = await downloadSportStatisticalReportPdf({
                    idevent,
                    idsport,
                })
                triggerBlobDownload(blob, filename)
                return
            }

            const options = await getSportReportOptions({ idevent, idsport })
            setReportOptions(options)
            setIsReportModalOpen(true)
        } catch (err) {
            const message = err?.unsupported
                ? 'Todavía no está disponible para este deporte.'
                : (err?.message || (isHandballBeach
                    ? 'No se pudo generar el reporte.'
                    : 'No se pudieron cargar las opciones del reporte.'))
            alert(message)
        } finally {
            setIsReportButtonLoading(false)
        }
    }

    const closeReportModal = () => {
        setIsReportModalOpen(false)
    }

    const toggleEditMode = () => {
        setEditMode(v => !v)
        // Opcional: al entrar a editar, asegurar que rankingValues esté sincronizado
        if (!editMode) {
            const map = {}
            const displayMap = {}
            instituciones.forEach(p => {
                map[p.idparticipant] = (p?.ranking ?? '') + ''
                displayMap[p.idparticipant] = p?.displayName ?? ''
            })
            setRankingValues(map)
            setDisplayNameValues(displayMap)
        }
    }

    const onChangeRanking = (idparticipant, value) => {
        setRankingValues(prev => ({ ...prev, [idparticipant]: value }))
    }

    const onChangeDisplayName = (idparticipant, value) => {
        setDisplayNameValues(prev => ({ ...prev, [idparticipant]: value }))
    }

    // Guarda ranking (comportamiento previo) y, si cambió, el nombre visual del participante (FTR-0006).
    const guardarParticipante = async (idparticipant) => {
        const raw = rankingValues[idparticipant]
        // Si está vacío => null. Si no, número (si falla parseo => null)
        const ranking = raw === '' ? null : (Number.isNaN(Number(raw)) ? null : Number(raw))

        const current = instituciones.find(p => p.idparticipant === idparticipant)
        const currentDisplayName = normalizeDisplayName(current?.displayName)
        const displayName = normalizeDisplayName(displayNameValues[idparticipant])
        const displayNameChanged = displayName !== currentDisplayName

        try {
            setSavingId(idparticipant)
            await axios.patch(`${API_BASE_URL}/updateparticipantranking`, {
                idparticipant,
                ranking,
            })

            if (displayNameChanged) {
                // Solo modifica participants.displayName; nunca institution.name/abrev.
                await axios.patch(`${API_BASE_URL}/updateparticipantdisplayname`, {
                    idparticipant,
                    displayName,
                })
            }

            // Reflejar en la tabla
            setInstituciones(prev =>
                prev.map(p =>
                    p.idparticipant === idparticipant ? { ...p, ranking, displayName } : p
                )
            )
            // Normalizamos los inputs a la representación que mostramos
            setRankingValues(prev => ({
                ...prev,
                [idparticipant]: ranking ?? ''
            }))
            setDisplayNameValues(prev => ({
                ...prev,
                [idparticipant]: displayName ?? ''
            }))
        } catch (e) {
            console.error('Error guardando participante:', e)
            alert(e?.response?.data?.message || 'No se pudo guardar el participante.')
        } finally {
            setSavingId(null)
        }
    }

    const actualizarTablaAcumulada = async () => {
        if (!cumulativeSeries || hasUnsavedRankingChanges) return
        setIsRecalculatingCumulative(true)
        try {
            const result = await recalculateCumulativeEvent({
                idevent,
                idsport,
                seriesId: cumulativeSeries.id,
            })
            toast.success(`¡Tabla acumulada actualizada! ${result.rowsUpdated || 0} filas.`)
        } catch (error) {
            toast.error(error?.response?.data?.msg || 'No se pudo actualizar la tabla acumulada.')
        } finally {
            setIsRecalculatingCumulative(false)
        }
    }

    const openImportModal = (institution) => {
        setSelectedInstitution(institution)
        setIsImportModalOpen(true)
    }

    const closeImportModal = () => {
        setIsImportModalOpen(false)
        setSelectedInstitution(null)
    }

    return (
        <>
            <div className="p-4 sm:p-6">
                <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:px-5">
                    <div>
                        <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#1e88e5]">Participación</p>
                        <h1 className="text-xl font-black tracking-tight text-slate-900 sm:text-2xl">
                            INSTITUCIONES PARTICIPANTES
                        </h1>
                        <p className="mt-1 text-xs font-medium text-slate-500">
                            Gestiona instituciones, fichas auxiliares y reportes estadísticos del evento.
                        </p>
                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row">
                        {individual && (
                            <SisdeuImportPanel
                                kind={SISDEU_PANEL_KINDS.PARTICIPANTS}
                                idevent={idevent}
                                idsport={idsport}
                                onImported={() => setParticipantsRevision(value => value + 1)}
                            />
                        )}
                        {/* FTR-0052: acreditados SISDEU de todas las instituciones participantes (oculto sin integración activa). */}
                        <SisdeuImportPanel
                            kind={SISDEU_PANEL_KINDS.BULK_ACCREDITATIONS}
                            idevent={idevent}
                            idsport={idsport}
                            participantsCount={participatingCount}
                            showMessage={!individual}
                        />
                        {editMode && cumulativeSeries && (
                            <button
                                type="button"
                                onClick={actualizarTablaAcumulada}
                                disabled={hasUnsavedRankingChanges || isRecalculatingCumulative}
                                className="inline-flex min-w-[210px] items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-black text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                                title={hasUnsavedRankingChanges ? 'Guarda primero los rankings pendientes' : `Actualizar ${cumulativeSeries.title}`}
                            >
                                {isRecalculatingCumulative ? <Spinner /> : <FiRefreshCw className="h-4 w-4" />}
                                {isRecalculatingCumulative ? 'Actualizando…' : 'Actualizar tabla acumulada'}
                            </button>
                        )}
                        {!individual && (
                        <button
                            type="button"
                            onClick={openReportModal}
                            disabled={isReportButtonLoading}
                            style={{ background: isReportButtonLoading ? '#5ea5ef' : PRIMARY }}
                            className="inline-flex min-w-[190px] items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-black text-white shadow-lg shadow-blue-500/20 transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-[#1e88e5] focus:ring-offset-2 disabled:cursor-not-allowed disabled:shadow-none"
                            title="Descargar reporte estadístico/general"
                        >
                            {isReportButtonLoading ? <Spinner /> : <FiDownloadCloud className="h-4 w-4" />}
                            {isReportButtonLoading
                                ? (isHandballBeach ? 'Generando PDF…' : 'Cargando…')
                                : 'Descargar reporte'}
                        </button>
                        )}
                    </div>
                </div>

                <div className="overflow-x-auto rounded-xl shadow-lg bg-white">
                    <table className="w-full table-auto divide-y divide-gray-200">
                        <thead style={{ background: PRIMARY }} className="text-white text-[11px] sm:text-xs">
                            <tr>
                                <th className="px-3 py-2 sm:px-5 sm:py-3 text-left font-semibold tracking-wide">Logo</th>
                                <th className="px-3 py-2 sm:px-5 sm:py-3 text-left font-semibold tracking-wide">Abrev.</th>
                                <th className="px-3 py-2 sm:px-5 sm:py-3 text-left font-semibold tracking-wide hidden sm:table-cell">
                                    Nombre
                                </th>

                                {editMode && (
                                    <th className="px-3 py-2 sm:px-5 sm:py-3 text-left font-semibold tracking-wide">
                                        Ranking
                                    </th>
                                )}

                                {editMode && (
                                    <th className="px-3 py-2 sm:px-5 sm:py-3 text-left font-semibold tracking-wide">
                                        Nombre del participante
                                    </th>
                                )}

                                <th className="px-3 py-2 sm:px-5 sm:py-3 text-left font-semibold tracking-wide">
                                    <div className="flex items-center gap-2">
                                        <span>Acciones</span>
                                        <button
                                            type="button"
                                            onClick={toggleEditMode}
                                            className="p-1 rounded hover:bg-white/10 transition"
                                            title={editMode ? 'Salir de modo editar' : 'Modo editar'}
                                        >
                                            {editMode ? <FiX className="h-4 w-4" /> : <FiEdit3 className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </th>
                            </tr>
                        </thead>

                        <tbody className="divide-y divide-gray-100 text-[13px] sm:text-sm">
                            {individual && instituciones.length === 0 && (
                                <tr>
                                    <td colSpan={editMode ? 6 : 4} className="px-3 py-8 text-center text-sm text-gray-500">
                                        0 instituciones participantes.
                                    </td>
                                </tr>
                            )}
                            {instituciones.map(({ idparticipant, Institution, ranking, displayName }) => {
                                const logo = `${Institution.path_base}${Institution.image_path}`
                                const isDownloading = loadingId === Institution.idinstitution
                                const isSaving = savingId === idparticipant

                                return (
                                    <tr key={idparticipant} className="even:bg-gray-50 hover:bg-gray-100">
                                        <td className="px-3 py-2 sm:px-5 sm:py-3">
                                            <button
                                                type="button"
                                                onClick={() => openImportModal(Institution)}
                                                className="inline-flex rounded-md transition hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-[#1e88e5] focus:ring-offset-2"
                                                title={`Agregar acreditados a ${Institution.name}`}
                                            >
                                                <img
                                                    src={logo}
                                                    alt={Institution.abrev}
                                                    className="h-8 w-8 sm:h-10 sm:w-10 object-contain rounded"
                                                />
                                            </button>
                                        </td>

                                        <td className="px-3 py-2 sm:px-5 sm:py-3 font-medium text-gray-800">
                                            <button
                                                type="button"
                                                onClick={() => openImportModal(Institution)}
                                                className="rounded-md text-left transition hover:text-[#1e88e5] focus:outline-none focus:ring-2 focus:ring-[#1e88e5] focus:ring-offset-2"
                                                title={`Agregar acreditados a ${Institution.name}`}
                                            >
                                                {Institution.abrev}
                                            </button>
                                        </td>

                                        <td className="px-3 py-2 sm:px-5 sm:py-3 text-gray-700 hidden sm:table-cell">
                                            {/* Nombre oficial de la institución (no se modifica desde aquí) */}
                                            {Institution.name}
                                            {normalizeDisplayName(displayName) && (
                                                <div
                                                    className="mt-0.5 text-[11px] font-medium text-[#1e88e5]"
                                                    title="Nombre visual del participante (reemplaza al nombre de la institución donde se muestra el nombre completo)"
                                                >
                                                    Se muestra como: {normalizeDisplayName(displayName)}
                                                </div>
                                            )}
                                        </td>

                                        {editMode && (
                                            <td className="px-3 py-2 sm:px-5 sm:py-3">
                                                <input
                                                    type="number"
                                                    inputMode="numeric"
                                                    className="w-24 rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e88e5]"
                                                    placeholder="--"
                                                    value={rankingValues[idparticipant] ?? ''}
                                                    onChange={e => onChangeRanking(idparticipant, e.target.value)}
                                                />
                                            </td>
                                        )}

                                        {editMode && (
                                            <td className="px-3 py-2 sm:px-5 sm:py-3">
                                                <input
                                                    type="text"
                                                    maxLength={DISPLAY_NAME_MAX_LENGTH}
                                                    className="w-48 sm:w-64 rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e88e5]"
                                                    placeholder={Institution.name}
                                                    value={displayNameValues[idparticipant] ?? ''}
                                                    onChange={e => onChangeDisplayName(idparticipant, e.target.value)}
                                                    aria-label={`Nombre del participante ${Institution.abrev}`}
                                                />
                                                <p className="mt-1 text-[11px] text-gray-500">
                                                    Vacío = usa el nombre de la institución. No cambia la abreviatura.
                                                </p>
                                            </td>
                                        )}

                                        <td className="px-3 py-2 sm:px-5 sm:py-3">
                                            <div className="flex flex-wrap items-center gap-2">
                                            {editMode && (
                                                <button
                                                    type="button"
                                                    onClick={() => guardarParticipante(idparticipant)}
                                                    disabled={isSaving}
                                                    className={`px-3 py-1.5 rounded-md text-xs sm:text-sm font-semibold text-white shadow
                            focus:outline-none focus:ring-2 focus:ring-[#1e88e5]
                            ${isSaving ? 'bg-[#5ea5ef] cursor-not-allowed' : 'bg-[#1e88e5] hover:bg-[#1976d2]'}`}
                                                >
                                                    {isSaving ? 'Guardando…' : 'Guardar'}
                                                </button>
                                            )}
                                            <button
                                                onClick={() => descargarInstitucion(Institution)}
                                                disabled={isDownloading}
                                                className={`relative w-32 flex items-center justify-center gap-2
                        px-3 py-1.5 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm font-semibold text-white
                        shadow focus:outline-none focus:ring-2 focus:ring-[#1e88e5]
                        ${isDownloading ? 'bg-[#5ea5ef] cursor-not-allowed' : 'bg-[#1e88e5] hover:bg-[#1976d2]'}`}
                                            >
                                                {isDownloading && (
                                                    <svg
                                                        className="absolute left-2 h-4 w-4 animate-spin"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <circle
                                                            className="opacity-25"
                                                            cx="12"
                                                            cy="12"
                                                            r="10"
                                                            stroke="currentColor"
                                                            strokeWidth="4"
                                                        />
                                                        <path
                                                            className="opacity-75"
                                                            fill="currentColor"
                                                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                                        />
                                                    </svg>
                                                )}
                                                {isDownloading ? 'Descargando…' : 'Ficha Auxiliar'}
                                            </button>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <ImportAccreditationsModal
                open={isImportModalOpen}
                onClose={closeImportModal}
                institution={selectedInstitution}
                idevent={idevent}
                idsport={idsport}
            />

            <ModalSportStatisticalReport
                open={isReportModalOpen}
                onClose={closeReportModal}
                idevent={idevent}
                idsport={idsport}
                options={reportOptions}
            />
        </>
    )
}

export default InstitucionesArea
